import { useCallback } from 'react';
import { type TypedLazyQueryTrigger } from '@reduxjs/toolkit/query/react';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  optimizationsSlice,
  type SimulationStateResponse,
  type ExecutionStateResponse,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

type PollingResponse =
  | { status: 'pending' }
  | { status: 'running' }
  | {
      status: 'ready';
      download: string;
    }
  | { status: 'failed' }
  | { status: 'expired' };

export const pollingSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    callPollingApi: build.query<
      PollingResponse,
      { url: string; method?: string; contentType?: string; body?: unknown }
    >({
      query: ({
        url,
        method = 'GET',
        body,
        contentType = 'application/json',
      }) => ({
        url,
        method,
        headers: {
          'Content-Type': contentType,
        },
        body: method === 'GET' ? undefined : body,
      }),
    }),
  }),
});

export const useCreatePollingRequest = <
  T extends TypedLazyQueryTrigger<{ statusURL: string }, any, any>,
>(
  fn: T
) => {
  const maxRequests = 100;
  const retryTimeout = 6000;
  const [callApi$] = pollingSlice.endpoints.callPollingApi.useLazyQuery();

  return useCallback(
    async (arg: Parameters<T>[0]) => {
      let requests = 0;

      const poll = async (url: string): Promise<string> => {
        const res = await callApi$({ url }).unwrap();
        switch (res.status) {
          case 'pending':
          case 'running': {
            if (requests === maxRequests) throw new Error('timeout');
            requests += 1;
            return new Promise((resolve) => {
              setTimeout(() => resolve(poll(url)), retryTimeout);
            });
          }
          case 'failed':
          case 'expired': {
            throw new Error(res.status);
          }
          case 'ready': {
            return res.download;
          }
          default: {
            throw new Error('unknown status');
          }
        }
      };
      return fn(arg)
        .unwrap()
        .then((res) => poll(res.statusURL));
    },
    [callApi$, fn, maxRequests, retryTimeout]
  );
};

/**
 * Hook for polling the simulation endpoint until the job status is "success" and results are available.
 * Supports cancellation via AbortSignal to stop polling when component unmounts.
 *
 * @param retryInterval - How often to poll in milliseconds (default: 6000ms / 6 seconds)
 * @param maxRequests - Maximum number of polling requests before timing out (default: 100)
 * @returns A function that takes appKey, optId, and optional abortSignal, starts polling, and resolves with the simulation state when complete
 */
export const usePollSimulation = (
  retryInterval: number = 6000,
  maxRequests: number = 100
) => {
  const [getSimulation] =
    optimizationsSlice.endpoints.getSimulation.useLazyQuery();

  return useCallback(
    async (
      appKey: string,
      optId: string,
      abortSignal?: AbortSignal
    ): Promise<SimulationStateResponse> => {
      let requests = 0;
      const timeoutIds: Set<ReturnType<typeof setTimeout>> = new Set();

      // Cleanup function to clear all timeouts
      const cleanup = () => {
        timeoutIds.forEach((id) => clearTimeout(id));
        timeoutIds.clear();
      };

      // Set up abort listener to clean up timeouts
      if (abortSignal) {
        abortSignal.addEventListener('abort', cleanup);
      }

      try {
        const poll = async (): Promise<SimulationStateResponse> => {
          // Check if cancelled before making API call
          if (abortSignal?.aborted) {
            throw new Error('Polling cancelled');
          }

          const res = await getSimulation({ appKey, optId }).unwrap();
          const jobStatus = res.optimization_state?.job?.status;
          const hasResults = res.optimization_state?.result !== null;

          // Check if job is successful and has results
          // Backend returns status as "SUCCEEDED" (uppercase) when job completes
          if (jobStatus === 'SUCCEEDED' && hasResults) {
            return res;
          }

          // Check if job failed or was cancelled
          // Backend returns "FAILED" or "CANCELLED" (uppercase) for terminal failure states
          if (jobStatus === 'FAILED' || jobStatus === 'CANCELLED') {
            throw new Error(`Simulation job ${jobStatus.toLowerCase()}`);
          }

          // Check if we've exceeded max requests
          if (requests >= maxRequests) {
            throw new Error(
              'Polling timeout: Maximum number of requests reached'
            );
          }

          // Check if cancelled before scheduling next poll
          if (abortSignal?.aborted) {
            throw new Error('Polling cancelled');
          }

          // Continue polling
          requests += 1;
          return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              timeoutIds.delete(timeoutId);
              if (abortSignal?.aborted) {
                reject(new Error('Polling cancelled'));
                return;
              }
              poll().then(resolve).catch(reject);
            }, retryInterval);
            timeoutIds.add(timeoutId);
          });
        };

        return await poll();
      } finally {
        // Cleanup all timeouts on exit
        cleanup();
        if (abortSignal) {
          abortSignal.removeEventListener('abort', cleanup);
        }
      }
    },
    [getSimulation, retryInterval, maxRequests]
  );
};

/**
 * Hook for polling the execution endpoint until the job status is "success" and results are available.
 * Supports cancellation via AbortSignal to stop polling when component unmounts.
 *
 * @param retryInterval - How often to poll in milliseconds (default: 6000ms / 6 seconds)
 * @param maxRequests - Maximum number of polling requests before timing out (default: 100)
 * @returns A function that takes appKey, optId, and optional abortSignal, starts polling, and resolves with the execution state when complete
 */
export const usePollExecution = (
  retryInterval: number = 6000,
  maxRequests: number = 100
) => {
  const [getExecution] =
    optimizationsSlice.endpoints.getExecution.useLazyQuery();

  return useCallback(
    async (
      appKey: string,
      optId: string,
      abortSignal?: AbortSignal
    ): Promise<ExecutionStateResponse> => {
      let requests = 0;
      const timeoutIds: Set<ReturnType<typeof setTimeout>> = new Set();

      // Cleanup function to clear all timeouts
      const cleanup = () => {
        timeoutIds.forEach((id) => clearTimeout(id));
        timeoutIds.clear();
      };

      // Set up abort listener to clean up timeouts
      if (abortSignal) {
        abortSignal.addEventListener('abort', cleanup);
      }

      try {
        const poll = async (): Promise<ExecutionStateResponse> => {
          // Check if cancelled before making API call
          if (abortSignal?.aborted) {
            throw new Error('Polling cancelled');
          }

          const res = await getExecution({ appKey, optId }).unwrap();
          const jobStatus = res.job?.status;
          const hasResults = res.result !== null;

          // Check if job is successful and has results
          // Backend returns status as "SUCCEEDED" (uppercase) when job completes
          if (jobStatus === 'SUCCEEDED' && hasResults) {
            return res;
          }

          // Check if job failed or was cancelled
          // Backend returns "FAILED" or "CANCELLED" (uppercase) for terminal failure states
          if (jobStatus === 'FAILED' || jobStatus === 'CANCELLED') {
            throw new Error(`Execution job ${jobStatus.toLowerCase()}`);
          }

          // Check if we've exceeded max requests
          if (requests >= maxRequests) {
            throw new Error(
              'Polling timeout: Maximum number of requests reached'
            );
          }

          // Check if cancelled before scheduling next poll
          if (abortSignal?.aborted) {
            throw new Error('Polling cancelled');
          }

          // Continue polling
          requests += 1;
          return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              timeoutIds.delete(timeoutId);
              if (abortSignal?.aborted) {
                reject(new Error('Polling cancelled'));
                return;
              }
              poll().then(resolve).catch(reject);
            }, retryInterval);
            timeoutIds.add(timeoutId);
          });
        };

        return await poll();
      } finally {
        // Cleanup all timeouts on exit
        cleanup();
        if (abortSignal) {
          abortSignal.removeEventListener('abort', cleanup);
        }
      }
    },
    [getExecution, retryInterval, maxRequests]
  );
};
